export const VECTORS = {
  link: {
    key: 'link',
    type: 'link',
    label: 'Link',
    format: null,
    failLabel: 'Clicked link',
    failVerb: 'click the link',
    luredPast: 'clicked the link',
    luredGerund: 'clicking',
    deepest: 'clicked',
    measure: 'full',
    counts: 'url',
  },
  attachmentHtml: {
    key: 'attachmentHtml',
    type: 'attachment',
    label: 'Attachment',
    format: 'HTML',
    failLabel: 'Attachment opened',
    failVerb: 'open the attachment',
    luredPast: 'opened the attachment',
    luredGerund: 'opening it',
    deepest: 'clicked',
    measure: 'full',
    counts: 'attachment',
  },
  attachmentPdf: {
    key: 'attachmentPdf',
    type: 'attachment',
    label: 'Attachment',
    format: 'PDF',
    failLabel: 'Clicked attachment',
    failVerb: 'click the link inside the file',
    luredPast: 'clicked the link inside the file',
    luredGerund: 'clicking',
    deepest: 'clicked',
    measure: 'floor',
    counts: 'attachment',
  },
  attachmentDocx: {
    key: 'attachmentDocx',
    type: 'attachment',
    label: 'Attachment',
    format: 'DOCX',
    failLabel: 'Clicked attachment',
    failVerb: 'click the link inside the file',
    luredPast: 'clicked the link inside the file',
    luredGerund: 'clicking',
    deepest: 'clicked',
    measure: 'floor',
    counts: 'attachment',
  },
  attachmentXls: {
    key: 'attachmentXls',
    type: 'attachment',
    label: 'Attachment',
    format: 'XLS',
    failLabel: null,
    failVerb: null,
    luredPast: null,
    luredGerund: null,
    deepest: null,
    measure: 'none',
    counts: 'xls',
  },
  dataEntry: {
    key: 'dataEntry',
    type: 'dataEntry',
    label: 'Data entry',
    format: null,
    failLabel: 'Clicked link',
    failVerb: 'click the link',
    luredPast: 'clicked the link',
    luredGerund: 'clicking',
    deepest: 'submitted',
    measure: 'full',
    counts: 'credentials',
  },
}

export const MEASURE_NOTE = {
  full: null,
  floor:
    'Only a click on the link inside the file is measured - a plain open is not tracked, ' +
    'so every rate here is a floor on true exposure.',
  none:
    'XLS attachments record nothing: the URL is plain cell text, so no recipient can be ' +
    'counted as lured and the campaign cannot be ranked.',
}

export const OUTCOME = {
  submitted: {
    key: 'submitted',
    label: 'Submitted data',
    tone: 'negative',
    color: 'var(--negative-color)',
    fail: true,
  },
  clicked: {
    key: 'clicked',
    label: 'Clicked',
    tone: 'warning',
    color: 'var(--warning-color)',
    fail: true,
  },
  reported: {
    key: 'reported',
    label: 'Reported',
    tone: 'positive',
    color: 'var(--positive-color, #12b76a)',
    win: true,
  },
  opened: { key: 'opened', label: 'Opened', tone: 'neutral', color: 'var(--sim-neutral, #B6B8C3)' },
  none: {
    key: 'none',
    label: 'No engagement',
    tone: 'neutral',
    color: 'var(--line-inert, #c2c7d6)',
  },
}

export const OUTCOME_ORDER = ['submitted', 'clicked', 'reported', 'opened', 'none']

export const RANK = {
  resilient: { key: 'resilient', label: 'Resilient', tone: 'positive', zone: 'good', needle: 84 },
  atRisk: { key: 'atRisk', label: 'At risk', tone: 'warning', zone: 'fair', needle: 50 },
  exposed: { key: 'exposed', label: 'Exposed', tone: 'negative', zone: 'poor', needle: 16 },
  notRated: { key: 'notRated', label: 'Not rated yet', tone: 'neutral', zone: 'none', needle: 50 },
}

export const rankFor = ({ phase, opened, delivered, clickRate, measure }) => {
  if (measure === 'none') return 'notRated'
  if (delivered === 0) return 'notRated'
  if (delivered < 10) return 'notRated'
  if (phase === 'sending') return 'notRated'
  if (opened < 5 || opened < delivered * 0.1) return 'notRated'
  if (clickRate <= 0.05) return 'resilient'
  if (clickRate <= 0.15) return 'atRisk'
  return 'exposed'
}

export const metricsFor = ({ counts, recovered, opened, enrolled }) => {
  const lured = counts.submitted + counts.clicked
  const reportedTotal = counts.reported
  return {
    lured,
    reportedTotal,
    clickRate: opened > 0 ? lured / opened : 0,
    reportRate: opened > 0 ? reportedTotal / opened : 0,
    resilience: lured > 0 ? reportedTotal / lured : null,
    recovered,
    remediationRate: enrolled.total > 0 ? enrolled.completed / enrolled.total : null,
  }
}

export const ATTACHMENT_CAVEAT = {
  HTML: 'Opening this file loads the landing page, so the open itself counts as lured.',
  PDF: 'Only a click on the link inside the file is measured. A plain open is invisible, so the numbers are a floor.',
  DOCX: 'Only a click on the link inside the file is measured. A plain open is invisible, so the numbers are a floor.',
  XLS: 'Nothing in this file is measured - the link is plain cell text - so nobody who received it can be counted as lured.',
}

export const greeting = (first) => `Hi ${first},`

export const attachmentFile = (vector, template = {}) => {
  if (!vector?.format) return null
  const ext =
    { HTML: 'html', PDF: 'pdf', DOCX: 'docx', XLS: 'xlsx' }[vector.format] ||
    vector.format.toLowerCase()
  const name = template.filename || (template.fileStem ? `${template.fileStem}.${ext}` : null)
  const size = template.fileStem
    ? { HTML: '12 KB', PDF: '84 KB', DOCX: '61 KB', XLS: '38 KB' }[vector.format] || null
    : null
  return { name, format: vector.format, size }
}
